"use client";

import { ArrowLeft, ArrowRight, ChevronLeft, ChevronRight, Play, X } from "lucide-react";
import Link from "next/link";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { Image } from "@/components/primitives";

import type { FeatureQuoteVideo } from "../content/reviews-page";

export type ResolvedFeatureQuoteTile = {
  id: string;
  name: string;
  image: string;
  quote: string;
  credentials: string;
  video?: FeatureQuoteVideo;
};

type FeatureQuoteTilesCarouselProps = {
  tiles: ReadonlyArray<ResolvedFeatureQuoteTile>;
  closeLabel: string;
  bookDemoLabel: string;
};

/** Width of the arrow-cursor hot zone at each window edge. Wide enough that the
 *  affordance appears well before the pointer reaches the edge. */
const getNavZoneWidth = () => Math.min(window.innerWidth * 0.32, 480);

/**
 * Identical copies of the tile set rendered back to back so the ribbon loops.
 * Three keeps a full set of runway either side of the middle one, which is where
 * the scroll position starts and is kept — so a drag can run a long way in
 * either direction before the wrap teleports it back.
 */
const LOOP_COPIES = 3;

/** One rendered tile instance. `uid` is per-copy, so the same advisor appearing
 *  three times stays three distinct slides — otherwise focusing one would open
 *  a video in every copy at once. */
type CarouselSlide = {
  readonly tile: ResolvedFeatureQuoteTile;
  readonly uid: string;
};

type NavSide = "left" | "right";

/** The arrow zones are a mouse-only affordance; touch keeps plain tap-to-play. */
const isFinePointer = () => window.matchMedia("(hover: hover) and (pointer: fine)").matches;

/** Which arrow zone (if any) a given x-coordinate falls in. Shared by the
 *  pointer tracking and the tile click guard so both agree on the boundaries. */
const resolveNavSide = (clientX: number, viewport: HTMLElement): NavSide | null => {
  const zoneWidth = getNavZoneWidth();
  if (clientX >= window.innerWidth - zoneWidth) return "right";
  // The back zone only exists once the track has actually been scrolled — at
  // rest the first tile is already flush and there is nothing to go back to.
  if (clientX <= zoneWidth && viewport.scrollLeft > 1) return "left";
  return null;
};

/** Id of the visible slide nearest the given edge — the one a step would bring
 *  fully into view, which is the tile that peeks under the arrow cursor. */
const getEdgeVisibleSlideId = (scroller: HTMLElement, edge: NavSide): string | null => {
  const scrollerRect = scroller.getBoundingClientRect();
  const slides = scroller.querySelectorAll<HTMLElement>("[data-slide-id]");

  let bestId: string | null = null;
  let best = edge === "right" ? -Infinity : Infinity;

  for (const slide of slides) {
    const rect = slide.getBoundingClientRect();
    const visible = rect.left < scrollerRect.right && rect.right > scrollerRect.left;
    if (!visible) continue;

    const candidate = edge === "right" ? rect.right : rect.left;
    if (edge === "right" ? candidate > best : candidate < best) {
      best = candidate;
      bestId = slide.dataset.slideId ?? null;
    }
  }

  return bestId;
};

/**
 * Width of one full copy of the tile set — the distance the ribbon must be
 * teleported to loop invisibly.
 *
 * Measured from the DOM rather than `scrollWidth / LOOP_COPIES`: the flex `gap`
 * sits *between* items, so the final copy is one gap short and dividing the
 * scroll width drifts a few px on every wrap. `offsetLeft` is relative to the
 * shared offset parent, so the difference is exact and independent of scroll.
 */
const getRepeatWidth = (scroller: HTMLElement, tileCount: number): number => {
  const items = scroller.querySelectorAll<HTMLElement>("li");
  if (items.length <= tileCount) return 0;
  return items[tileCount].offsetLeft - items[0].offsetLeft;
};

/** Distance between adjacent slide left edges — the snap-grid step. */
const getStride = (scroller: HTMLElement, viewport: HTMLElement): number => {
  const slides = scroller.querySelectorAll<HTMLElement>("li");
  if (slides.length > 1) {
    return slides[1].getBoundingClientRect().left - slides[0].getBoundingClientRect().left;
  }
  return viewport.clientWidth / 3;
};

const FeatureQuoteTilesCarousel = ({
  tiles,
  closeLabel,
  bookDemoLabel,
}: FeatureQuoteTilesCarouselProps) => {
  const scrollerRef = useRef<HTMLUListElement>(null);
  const viewportRef = useRef<HTMLDivElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const dragStateRef = useRef<{ startX: number; startScrollLeft: number } | null>(null);
  const didDragRef = useRef(false);
  const momentumRef = useRef<number | null>(null);
  const [navSide, setNavSide] = useState<NavSide | null>(null);
  const [cursorPosition, setCursorPosition] = useState({ x: 0, y: 0 });
  const [navZoneBounds, setNavZoneBounds] = useState<{ top: number; height: number } | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  /** The card physically under the pointer when the drag started — it keeps its
   *  lift for the whole drag instead of dropping the moment :hover moves off. */
  const [grabbedId, setGrabbedId] = useState<string | null>(null);
  const [navHoverSlideId, setNavHoverSlideId] = useState<string | null>(null);
  const [focusedId, setFocusedId] = useState<string | null>(null);
  const hoverVideoRefs = useRef(new Map<string, HTMLVideoElement>());

  /**
   * The looping ribbon is desktop-only — mobile keeps the original single pass
   * with its leading inset and trailing runway, which the focus-mode centring
   * depends on. Starts false so SSR and the first client render agree; the
   * effect promotes it on desktop.
   */
  const [isLooping, setIsLooping] = useState(false);

  useEffect(() => {
    const query = window.matchMedia("(min-width: 768px)");
    const sync = () => setIsLooping(query.matches);

    sync();
    query.addEventListener("change", sync);
    return () => query.removeEventListener("change", sync);
  }, []);

  const slides = useMemo<ReadonlyArray<CarouselSlide>>(
    () =>
      Array.from({ length: isLooping ? LOOP_COPIES : 1 }).flatMap((_, copy) =>
        tiles.map((tile) => ({ tile, uid: `${tile.id}#${copy}` })),
      ),
    [tiles, isLooping],
  );

  /**
   * Keeps the scroll position inside the middle copy by teleporting it whole
   * set-widths. The copies are identical, so the jump is invisible. Skipped in
   * focus mode, where a centred tile must stay put.
   */
  const wrapScroll = useCallback(() => {
    const viewport = viewportRef.current;
    const scroller = scrollerRef.current;
    if (!viewport || !scroller) return;

    const repeat = getRepeatWidth(scroller, tiles.length);
    if (repeat <= 0) return;

    let delta = 0;
    if (viewport.scrollLeft >= repeat * 2) delta = -repeat;
    else if (viewport.scrollLeft <= 1) delta = repeat;
    if (delta === 0) return;

    viewport.scrollLeft += delta;
    // Re-anchor an in-flight drag, or the next pointermove would snap the
    // position straight back to where it was before the teleport.
    if (dragStateRef.current) dragStateRef.current.startScrollLeft += delta;
  }, [tiles.length]);

  // Desktop hover preview: play the short muted clip from the start; the CSS
  // fades it in over the still image. Suppressed while dragging or in focus.
  const playHoverPreview = useCallback(
    (id: string) => {
      if (isDragging || focusedId) return;
      const el = hoverVideoRefs.current.get(id);
      if (!el) return;
      el.currentTime = 0;
      void el.play().catch(() => {});
    },
    [isDragging, focusedId],
  );

  const stopHoverPreview = useCallback((id: string) => {
    const el = hoverVideoRefs.current.get(id);
    if (!el) return;
    el.pause();
    el.currentTime = 0;
  }, []);

  // Warm every hover preview once the page has loaded and the browser is idle,
  // so the first hover plays instantly instead of lagging behind its fetch.
  // The clips are tiny (~0.1–0.4MB each) but this still waits for `load` + an
  // idle slot so it never competes with the hero image, and skips touch
  // devices, which never show the previews.
  useEffect(() => {
    if (!window.matchMedia("(hover: hover) and (pointer: fine)").matches) return;

    let idleId: number | null = null;
    let timeoutId: number | null = null;

    const warm = () => {
      for (const el of hoverVideoRefs.current.values()) {
        el.preload = "auto";
        el.load();
      }
    };

    const schedule = () => {
      if (typeof window.requestIdleCallback === "function") {
        idleId = window.requestIdleCallback(warm, { timeout: 3000 });
      } else {
        timeoutId = window.setTimeout(warm, 1500);
      }
    };

    if (document.readyState === "complete") {
      schedule();
    } else {
      window.addEventListener("load", schedule, { once: true });
    }

    return () => {
      window.removeEventListener("load", schedule);
      if (idleId !== null && typeof window.cancelIdleCallback === "function") {
        window.cancelIdleCallback(idleId);
      }
      if (timeoutId !== null) window.clearTimeout(timeoutId);
    };
  }, []);

  // Park the ribbon in the middle copy so it can be dragged a long way either
  // direction before the wrap kicks in.
  useEffect(() => {
    const viewport = viewportRef.current;
    const scroller = scrollerRef.current;
    if (!viewport || !scroller) return;

    const repeat = getRepeatWidth(scroller, tiles.length);
    if (repeat <= 0) return;

    // Half a tile past the copy boundary, so the left edge opens on a dial cut
    // through the middle rather than one flush to the window. Matches the
    // desktop `scroll-padding-left`, so snapping holds the same offset.
    const firstSlide = scroller.querySelector<HTMLElement>("li");
    viewport.scrollLeft = repeat + (firstSlide ? firstSlide.offsetWidth / 2 : 0);
  }, [tiles.length, slides.length]);

  useEffect(() => {
    const viewport = viewportRef.current;
    if (!viewport || focusedId) return;

    const onScroll = () => wrapScroll();
    viewport.addEventListener("scroll", onScroll, { passive: true });
    return () => viewport.removeEventListener("scroll", onScroll);
  }, [wrapScroll, focusedId]);

  const scrollByOneCard = useCallback(
    (direction: 1 | -1) => {
      const viewport = viewportRef.current;
      const scroller = scrollerRef.current;
      if (!viewport || !scroller || isDragging) return;

      // No end to run off: the ribbon loops, so a step is always just one
      // snap-grid stride and the scroll handler wraps at the set boundary.
      viewport.scrollBy({ left: direction * getStride(scroller, viewport), behavior: "smooth" });
    },
    [isDragging],
  );

  // Center the focused tile in the viewport (scroll-snap is disabled in focus
  // mode via CSS, so the centered position holds instead of re-snapping left).
  const centerSlide = useCallback((slideIndex: number) => {
    const viewport = viewportRef.current;
    const scroller = scrollerRef.current;
    if (!viewport || !scroller) return;

    const slide = scroller.querySelectorAll<HTMLElement>("li")[slideIndex];
    if (!slide) return;

    const viewportRect = viewport.getBoundingClientRect();
    const slideRect = slide.getBoundingClientRect();
    const delta = slideRect.left - viewportRect.left - (viewport.clientWidth - slideRect.width) / 2;
    viewport.scrollBy({ left: delta, behavior: "smooth" });
  }, []);

  const enterFocus = useCallback(
    (slide: CarouselSlide, slideIndex: number) => {
      if (!slide.tile.video || didDragRef.current) return;
      setFocusedId(slide.uid);
      setNavSide(null);
      setNavHoverSlideId(null);
      // Defer so the focus class (which disables scroll-snap) lands first.
      requestAnimationFrame(() => centerSlide(slideIndex));
    },
    [centerSlide],
  );

  const exitFocus = useCallback(() => setFocusedId(null), []);

  // Step focus to the previous/next tile (the mobile chevrons over the peeking
  // videos) — centers and plays it with the same eased motion as a tap. Because
  // the copies sit end to end, stepping past the end of one runs into the next.
  const focusRelative = useCallback(
    (offset: number) => {
      const currentIndex = slides.findIndex((slide) => slide.uid === focusedId);
      if (currentIndex < 0) return;
      const nextIndex = currentIndex + offset;
      const nextSlide = slides[nextIndex];
      if (nextSlide) enterFocus(nextSlide, nextIndex);
    },
    [slides, focusedId, enterFocus],
  );

  const focusedIndex = focusedId ? slides.findIndex((slide) => slide.uid === focusedId) : -1;
  const hasPrev = focusedIndex > 0;
  const hasNext = focusedIndex >= 0 && focusedIndex < slides.length - 1;

  // Focus mode: lock page scroll and exit on Escape.
  useEffect(() => {
    if (!focusedId) return;

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") setFocusedId(null);
    };
    document.addEventListener("keydown", onKeyDown);
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    // Lifts the hero panel (and its veil) above the fixed marketing header so
    // the mocha covers the whole site, not just everything below the header.
    document.body.classList.add("reviews-lights-off");

    return () => {
      document.removeEventListener("keydown", onKeyDown);
      document.body.style.overflow = previousOverflow;
      document.body.classList.remove("reviews-lights-off");
    };
  }, [focusedId]);

  // Mobile focus mode: a fling coasts on native momentum, then once it stops the
  // nearest tile eases into the center with a smooth scroll (the same motion as
  // tapping a peeking tile). Once it's centered, focus it so its video plays —
  // flowing through the testimonials swipe-by-swipe without tapping each one.
  useEffect(() => {
    if (!focusedId) return;
    if (!window.matchMedia("(max-width: 767px)").matches) return;

    const viewport = viewportRef.current;
    const scroller = scrollerRef.current;
    if (!viewport || !scroller) return;

    let settleTimer: number | null = null;
    const onScroll = () => {
      if (settleTimer !== null) window.clearTimeout(settleTimer);
      // ~130ms of no scroll events ≈ the fling (or the ease) has finished.
      settleTimer = window.setTimeout(() => {
        const viewportRect = viewport.getBoundingClientRect();
        const centerX = viewportRect.left + viewportRect.width / 2;

        let bestSlide: HTMLElement | null = null;
        let bestDelta = Infinity;
        for (const slide of scroller.querySelectorAll<HTMLElement>("[data-slide-id]")) {
          const rect = slide.getBoundingClientRect();
          const delta = rect.left + rect.width / 2 - centerX;
          if (Math.abs(delta) < Math.abs(bestDelta)) {
            bestDelta = delta;
            bestSlide = slide;
          }
        }
        if (!bestSlide) return;

        // Not centered yet: ease the nearest tile into the middle. The scroll it
        // triggers resets this timer, so the next settle (once it lands) focuses.
        if (Math.abs(bestDelta) > 4) {
          viewport.scrollBy({ left: bestDelta, behavior: "smooth" });
          return;
        }

        const bestId = bestSlide.dataset.slideId ?? null;
        if (bestId && bestId !== focusedId) {
          const slide = slides.find((candidate) => candidate.uid === bestId);
          if (slide?.tile.video) setFocusedId(bestId);
        }
      }, 130);
    };

    viewport.addEventListener("scroll", onScroll, { passive: true });
    return () => {
      viewport.removeEventListener("scroll", onScroll);
      if (settleTimer !== null) window.clearTimeout(settleTimer);
    };
  }, [focusedId, slides]);

  // Mouse drag-to-scroll via window listeners rather than setPointerCapture:
  // capturing then releasing a mouse pointer SUPPRESSES the trailing click
  // entirely, so tiles could never open their video. Window listeners keep the
  // drag working past the track edges while leaving the click intact.
  const handleTrackPointerDown = useCallback((event: React.PointerEvent<HTMLUListElement>) => {
    didDragRef.current = false;
    if (event.button !== 0 || event.pointerType !== "mouse") return;

    const viewport = viewportRef.current;
    if (!viewport) return;

    // A fresh grab cancels any glide still coasting from the last flick.
    if (momentumRef.current !== null) {
      cancelAnimationFrame(momentumRef.current);
      momentumRef.current = null;
    }

    // Remember which card was grabbed so it stays lifted for the whole drag.
    const grabbedSlide = (event.target as HTMLElement).closest<HTMLElement>("[data-slide-id]");
    setGrabbedId(grabbedSlide?.dataset.slideId ?? null);

    dragStateRef.current = { startX: event.clientX, startScrollLeft: viewport.scrollLeft };
    // Track pointer velocity (px per ms) so the release can coast.
    let lastX = event.clientX;
    let lastT = performance.now();
    let velocity = 0;

    const onMove = (moveEvent: PointerEvent) => {
      const dragState = dragStateRef.current;
      if (!dragState) return;
      const deltaX = moveEvent.clientX - dragState.startX;
      if (Math.abs(deltaX) > 6 && !didDragRef.current) {
        didDragRef.current = true;
        setIsDragging(true);
        setNavSide(null);
        setNavHoverSlideId(null);
      }
      if (didDragRef.current) {
        viewport.scrollLeft = dragState.startScrollLeft - deltaX;
        const now = performance.now();
        const elapsed = now - lastT;
        if (elapsed > 0) velocity = (moveEvent.clientX - lastX) / elapsed;
        lastX = moveEvent.clientX;
        lastT = now;
      }
    };

    const onUp = () => {
      dragStateRef.current = null;
      window.removeEventListener("pointermove", onMove);
      window.removeEventListener("pointerup", onUp);

      // A flick keeps gliding and eases to a stop before scroll-snap re-engages;
      // a slow release just settles. isDragging stays true through the glide so
      // mandatory snapping doesn't cancel it mid-flight.
      if (didDragRef.current && Math.abs(velocity) > 0.05) {
        const maxScroll = viewport.scrollWidth - viewport.clientWidth;
        let glide = velocity;
        let prevT = performance.now();
        const step = (now: number) => {
          const frame = Math.min(now - prevT, 32);
          prevT = now;
          glide *= Math.pow(0.92, frame / 16); // frame-rate-independent friction
          viewport.scrollLeft -= glide * frame;
          const atEdge = viewport.scrollLeft <= 0 || viewport.scrollLeft >= maxScroll;
          if (Math.abs(glide) > 0.02 && !atEdge) {
            momentumRef.current = requestAnimationFrame(step);
          } else {
            momentumRef.current = null;
            setIsDragging(false);
            setGrabbedId(null);
          }
        };
        momentumRef.current = requestAnimationFrame(step);
      } else {
        // Defer so the click handler (fired after pointerup) still sees the drag.
        requestAnimationFrame(() => {
          setIsDragging(false);
          setGrabbedId(null);
        });
      }
    };

    window.addEventListener("pointermove", onMove);
    window.addEventListener("pointerup", onUp);
  }, []);

  // Stop any in-flight glide if the carousel unmounts.
  useEffect(() => {
    return () => {
      if (momentumRef.current !== null) cancelAnimationFrame(momentumRef.current);
    };
  }, []);

  // A drag ends with didDragRef set, so the click that follows is ignored;
  // a clean click resolves its tile from the event target and opens its video.
  const handleTrackClick = useCallback(
    (event: React.MouseEvent<HTMLUListElement>) => {
      if (didDragRef.current) return;

      // A click inside a live arrow zone steps the carousel and must never open
      // the tile underneath. The zone is pointer-transparent until React commits
      // the armed state, so a quick click lands here instead — take the step
      // explicitly rather than swallowing the click, or it does nothing at all.
      //
      // Focus mode is exempt: the zones are disarmed there and the dimmed side
      // tiles stay selectable, so an edge-positioned tile must still reach
      // enterFocus. Desktop only — on touch there are no arrow zones and every
      // tile stays tap-to-play.
      const viewport = viewportRef.current;
      if (!focusedId && viewport && isFinePointer()) {
        const side = resolveNavSide(event.clientX, viewport);
        if (side) {
          scrollByOneCard(side === "left" ? -1 : 1);
          return;
        }
      }

      const slide = (event.target as HTMLElement).closest<HTMLElement>("[data-slide-id]");
      const id = slide?.dataset.slideId;
      if (!id) return;
      const index = slides.findIndex((slide) => slide.uid === id);
      if (index >= 0) enterFocus(slides[index], index);
    },
    [slides, enterFocus, focusedId, scrollByOneCard],
  );

  useEffect(() => {
    const syncNavZoneBounds = () => {
      const container = containerRef.current;
      if (!container) return;

      const bounds = container.getBoundingClientRect();
      setNavZoneBounds({ top: bounds.top, height: bounds.height });
    };

    syncNavZoneBounds();
    window.addEventListener("scroll", syncNavZoneBounds, { passive: true });
    window.addEventListener("resize", syncNavZoneBounds);

    return () => {
      window.removeEventListener("scroll", syncNavZoneBounds);
      window.removeEventListener("resize", syncNavZoneBounds);
    };
  }, []);

  useEffect(() => {
    // The arrow-cursor zones are a mouse-only affordance — skip them entirely
    // on touch / coarse-pointer devices so they never activate off desktop.
    if (!window.matchMedia("(hover: hover) and (pointer: fine)").matches) return;

    const handlePointerMove = (event: PointerEvent) => {
      if (isDragging || dragStateRef.current || focusedId) {
        setNavSide(null);
        setNavHoverSlideId(null);
        return;
      }

      const container = containerRef.current;
      const viewport = viewportRef.current;
      if (!container || !viewport) return;

      const bounds = container.getBoundingClientRect();
      const inVerticalBand = event.clientY >= bounds.top && event.clientY <= bounds.bottom;
      const side = inVerticalBand ? resolveNavSide(event.clientX, viewport) : null;

      setNavSide(side);
      if (side) {
        setCursorPosition({ x: event.clientX, y: event.clientY });
        setNavHoverSlideId(getEdgeVisibleSlideId(viewport, side));
      } else {
        setNavHoverSlideId(null);
      }
    };

    const handlePointerLeave = () => {
      if (!isDragging) {
        setNavSide(null);
        setNavHoverSlideId(null);
      }
    };

    window.addEventListener("pointermove", handlePointerMove);
    window.addEventListener("blur", handlePointerLeave);

    const viewport = viewportRef.current;
    const handleScroll = () => {
      if (!navSide || !viewport) return;
      // Scrolling back to the start retires the back zone out from under the
      // pointer, so the cursor doesn't linger over a step that can't happen.
      if (navSide === "left" && viewport.scrollLeft <= 1) {
        setNavSide(null);
        setNavHoverSlideId(null);
        return;
      }
      setNavHoverSlideId(getEdgeVisibleSlideId(viewport, navSide));
    };
    viewport?.addEventListener("scroll", handleScroll, { passive: true });

    return () => {
      window.removeEventListener("pointermove", handlePointerMove);
      window.removeEventListener("blur", handlePointerLeave);
      viewport?.removeEventListener("scroll", handleScroll);
    };
  }, [isDragging, navSide, focusedId]);

  return (
    <div
      className={`reviews-feature-carousel relative ${
        navSide && !isDragging ? "reviews-feature-carousel--nav-active" : ""
      } ${focusedId ? "reviews-feature-carousel--focus" : ""}`}
      ref={containerRef}
    >
      {focusedId && (
        <>
          {/* Lights off: mocha veil over the whole site; click anywhere on it to exit. */}
          <div aria-hidden="true" className="reviews-focus-overlay" onClick={exitFocus} />
          <button
            aria-label={closeLabel}
            className="reviews-focus-close bg-champagne-white/90 text-mocha hover:bg-champagne-white flex size-11 items-center justify-center rounded-full transition-colors"
            onClick={exitFocus}
            type="button"
          >
            <X aria-hidden="true" className="size-5" strokeWidth={1.75} />
          </button>

          {/* Mobile only: minimal prev/next chevrons centered in the gap between
              the focused video and the screen edge. */}
          {hasPrev && (
            <button
              aria-label="Previous testimonial"
              className="reviews-focus-nav reviews-focus-nav--prev text-champagne-white flex items-center justify-center md:hidden"
              onClick={() => focusRelative(-1)}
              type="button"
            >
              <ChevronLeft aria-hidden="true" className="size-8" strokeWidth={1.5} />
            </button>
          )}
          {hasNext && (
            <button
              aria-label="Next testimonial"
              className="reviews-focus-nav reviews-focus-nav--next text-champagne-white flex items-center justify-center md:hidden"
              onClick={() => focusRelative(1)}
              type="button"
            >
              <ChevronRight aria-hidden="true" className="size-8" strokeWidth={1.5} />
            </button>
          )}

          {/* Mobile only: outline demo CTA centered below the focused video. */}
          <div className="reviews-focus-cta md:hidden">
            <Link
              className="prelaunch-cta prelaunch-cta--stroke"
              href="https://info.ageless.ai/live-product-demo"
            >
              <span className="prelaunch-cta-label whitespace-nowrap">{bookDemoLabel}</span>
            </Link>
          </div>
        </>
      )}

      <div className="reviews-feature-carousel__viewport" ref={viewportRef}>
        <ul
          className={`reviews-feature-carousel__track m-0 flex list-none gap-3 p-0 ${
            isDragging ? "reviews-feature-carousel__track--dragging" : ""
          }`}
          onClick={handleTrackClick}
          onPointerDown={handleTrackPointerDown}
          ref={scrollerRef}
        >
          {slides.map((slide, slideIndex) => {
            const { tile, uid } = slide;
            const isNavPeek = !focusedId && navHoverSlideId === uid;
            const isFocused = focusedId === uid;
            const isDimmed = Boolean(focusedId) && !isFocused;

            return (
              <li
                className={`reviews-feature-carousel__slide shrink-0 snap-start ${
                  isNavPeek ? "reviews-feature-carousel__slide--nav-peek" : ""
                } ${isDimmed ? "reviews-feature-carousel__slide--dim" : ""} ${
                  grabbedId === uid ? "reviews-feature-carousel__slide--grabbed" : ""
                }`}
                data-slide-id={uid}
                key={uid}
              >
                <div
                  className={`reviews-feature-carousel__slide-surface ${
                    tile.video && !isFocused ? "cursor-pointer" : ""
                  }`}
                  onKeyDown={(event) => {
                    if (event.key !== "Enter" && event.key !== " ") return;
                    event.preventDefault();
                    if (isFocused) return;
                    enterFocus(slide, slideIndex);
                  }}
                  onMouseEnter={() => playHoverPreview(uid)}
                  onMouseLeave={() => stopHoverPreview(uid)}
                  role={tile.video && !isFocused ? "button" : undefined}
                  tabIndex={tile.video && !isFocused ? 0 : undefined}
                >
                  <figure className="reviews-feature-tile relative m-0 overflow-hidden">
                    <Image
                      alt={tile.name}
                      className="reviews-feature-tile__image object-cover object-top"
                      fill
                      sizes="375px"
                      src={tile.image}
                    />
                    {isFocused && tile.video ? (
                      /* Self-hosted testimonial; loads only on click, in-frame. */
                      <video
                        autoPlay
                        className="bg-mocha absolute inset-0 z-30 h-full w-full object-cover object-top"
                        controls
                        playsInline
                        poster={tile.image}
                        preload="auto"
                        src={tile.video.full}
                        title={tile.name}
                      />
                    ) : (
                      <>
                        {tile.video && (
                          /* Muted preview that fades in on desktop hover (CSS). */
                          <video
                            aria-hidden="true"
                            className="reviews-feature-tile__hover-video pointer-events-none absolute inset-0 z-[1] h-full w-full object-cover object-top"
                            loop
                            playsInline
                            preload="none"
                            ref={(el) => {
                              const map = hoverVideoRefs.current;
                              if (el) {
                                el.muted = true;
                                map.set(uid, el);
                              } else {
                                map.delete(uid);
                              }
                            }}
                          >
                            <source src={tile.video.hoverWebm} type="video/webm" />
                            <source src={tile.video.hoverMp4} type="video/mp4" />
                          </video>
                        )}
                        <div
                          aria-hidden="true"
                          className="reviews-feature-tile__image-fade pointer-events-none absolute inset-0 z-[2]"
                        />
                        <div className="reviews-feature-tile__quote-row absolute inset-x-0 top-0 z-20">
                          <span
                            aria-hidden="true"
                            className="reviews-feature-tile__mark text-mocha/50"
                          >
                            &ldquo;
                          </span>
                          <blockquote className="reviews-feature-tile__quote text-mocha m-0 text-pretty">
                            {tile.quote}
                          </blockquote>
                        </div>
                        <div
                          aria-hidden="true"
                          className="reviews-feature-tile__scrim pointer-events-none absolute inset-x-0 bottom-0 z-10"
                        />
                        <figcaption className="reviews-feature-tile__footer absolute inset-x-0 bottom-0 z-20">
                          <div className="reviews-feature-tile__meta min-w-0 flex-1">
                            <p className="reviews-feature-tile__name text-champagne-white m-0">
                              {tile.name}
                            </p>
                            <p className="reviews-feature-tile__credentials text-champagne-white/85 mt-1.5 mb-0">
                              {tile.credentials}
                            </p>
                          </div>
                          {tile.video && (
                            <span
                              aria-hidden="true"
                              className="reviews-feature-tile__play bg-champagne-white text-mocha flex shrink-0 items-center justify-center rounded-full"
                            >
                              <Play className="ml-0.5 fill-current" />
                            </span>
                          )}
                        </figcaption>
                      </>
                    )}
                  </figure>
                </div>
              </li>
            );
          })}
        </ul>
      </div>

      {/* Each zone only takes pointer events while its own arrow cursor is up,
          so the inactive edge stays a plain drag surface. */}
      <div
        aria-label="Previous testimonials"
        className="reviews-feature-carousel__nav-zone reviews-feature-carousel__nav-zone--prev"
        onClick={() => scrollByOneCard(-1)}
        role="presentation"
        style={
          navZoneBounds && !focusedId
            ? {
                top: navZoneBounds.top,
                height: navZoneBounds.height,
                pointerEvents: navSide === "left" && !isDragging ? "auto" : "none",
              }
            : { pointerEvents: "none" }
        }
      />

      <div
        aria-label="Next testimonials"
        className="reviews-feature-carousel__nav-zone reviews-feature-carousel__nav-zone--next"
        onClick={() => scrollByOneCard(1)}
        role="presentation"
        style={
          navZoneBounds && !focusedId
            ? {
                top: navZoneBounds.top,
                height: navZoneBounds.height,
                pointerEvents: navSide === "right" && !isDragging ? "auto" : "none",
              }
            : { pointerEvents: "none" }
        }
      />

      <span
        aria-hidden="true"
        className={`reviews-feature-carousel__cursor bg-champagne-white text-mocha pointer-events-none fixed z-[80] flex size-14 items-center justify-center rounded-full shadow-[0_10px_30px_rgba(40,29,26,0.18)] transition-opacity duration-200 ${
          navSide && !isDragging ? "opacity-100" : "opacity-0"
        }`}
        style={{
          left: cursorPosition.x,
          top: cursorPosition.y,
          transform: "translate(-50%, -50%)",
        }}
      >
        {navSide === "left" ? (
          <ArrowLeft className="size-5" strokeWidth={1.75} />
        ) : (
          <ArrowRight className="size-5" strokeWidth={1.75} />
        )}
      </span>
    </div>
  );
};

export default FeatureQuoteTilesCarousel;
